/**
 * Review notes on lines (D341): the pure half. A note is a file, a side, a line or a range and a body; it is a
 * draft until it is sent, then waits for the agent's answer, which says `path:line — done` or `path:line — skipped
 * (why)` for each. Nothing here touches a disk, a socket or the DOM, so the host store, the renderer and the tests
 * share one set of rules. The transport (`review.notes.send`, D343) addresses a line the same way: path, side, line.
 */
export const MAX_NOTE_CHARS = 2000;
export const MAX_NOTES = 50;
/** A range with its ends the right way round, a single line when both ends agree. */
export function normaliseRange(a, b) {
    if (b === undefined || b === a)
        return { line: a };
    return { line: Math.min(a, b), endLine: Math.max(a, b) };
}
/** Add a note as a draft. Empty text and a full list add nothing. */
export function addNote(notes, input, now, id) {
    const text = input.text.trim().slice(0, MAX_NOTE_CHARS);
    if (!text || !input.path || notes.length >= MAX_NOTES)
        return [...notes];
    const range = normaliseRange(input.line, input.endLine);
    return [...notes, { id, path: input.path, side: input.side, ...range, text, status: "draft", createdAt: now }];
}
/** Change a draft's text. A note that was sent is not edited; send a new one. Emptied text is a delete. */
export function editNote(notes, id, text) {
    const next = text.trim().slice(0, MAX_NOTE_CHARS);
    if (!next)
        return deleteNote(notes, id);
    return notes.map((n) => (n.id === id && n.status === "draft" ? { ...n, text: next } : n));
}
export function deleteNote(notes, id) {
    return notes.filter((n) => n.id !== id);
}
/** Draft again: a sent note the person wants to re-send. Done and skipped notes reopen the same way. */
export function reopenNote(notes, id) {
    return notes.map((n) => {
        if (n.id !== id || n.status === "draft")
            return n;
        const { sentAt: _s, sentTo: _t, reply: _r, ...rest } = n;
        return { ...rest, status: "draft" };
    });
}
export const isResolved = (n) => n.status === "done" || n.status === "skipped";
export const drafts = (notes) => notes.filter((n) => n.status === "draft");
/** Notes still open: drafts and sent-but-unanswered. */
export const openNotes = (notes) => notes.filter((n) => !isResolved(n));
export function notesForFile(notes, path) {
    return notes.filter((n) => n.path === path);
}
/** How many open notes each file carries, for the badge beside its name. */
export function openCountByFile(notes) {
    const out = new Map();
    for (const n of openNotes(notes))
        out.set(n.path, (out.get(n.path) ?? 0) + 1);
    return out;
}
export function noteCoversLine(n, path, side, line) {
    return n.path === path && n.side === side && line >= n.line && line <= (n.endLine ?? n.line);
}
/** `src/a.ts:12`, `src/a.ts:12-15`, with "(before the change)" on a removed line. */
export function noteAddress(n) {
    const range = n.endLine && n.endLine !== n.line ? `${n.line}-${n.endLine}` : `${n.line}`;
    return `${n.path}:${range}${n.side === "old" ? " (before the change)" : ""}`;
}
/** Mark notes as sent. Only drafts move; the rest are left as they are. */
export function markSent(notes, ids, at, to) {
    return notes.map((n) => (ids.has(n.id) && n.status === "draft" ? { ...n, status: "sent", sentAt: at, sentTo: to } : n));
}
export const fileCount = (notes) => new Set(notes.map((n) => n.path)).size;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** The one sentence a batch starts with. */
export function batchPreface(notes, person) {
    const who = person?.trim() ? person.trim() : "the reviewer";
    return `Review notes from ${who}, ${notes.length} on ${plural(fileCount(notes), "file")}; address each and say which you changed.`;
}
function groupedLines(notes) {
    const byFile = new Map();
    for (const n of notes)
        byFile.set(n.path, [...(byFile.get(n.path) ?? []), n]);
    const out = [];
    for (const [file, group] of byFile) {
        out.push(`### ${file}`);
        for (const n of [...group].sort((a, b) => a.line - b.line))
            out.push(`- \`${noteAddress(n)}\` — ${n.text.trim().replace(/\r?\n/g, "\n  ")}`);
        out.push("");
    }
    return out;
}
/** One Markdown block, grouped by file, each note as `path:line` and its text. Sent to an agent as one message. */
export function renderBatch(notes, person) {
    return [batchPreface(notes, person), "", ...groupedLines(notes), "When you reply, list each note as `path:line — done` or `path:line — skipped (why)`."].join("\n");
}
/** The same notes as one pull request comment: no instruction to an agent, each line addressed. */
export function renderPullRequestComment(notes, person) {
    const who = person?.trim() ? ` from ${person.trim()}` : "";
    return [`Review notes${who}, ${notes.length} on ${plural(fileCount(notes), "file")}.`, "", ...groupedLines(notes)].join("\n").trimEnd();
}
const RESOLUTION = /^\s*(?:[-*+]|\d+[.)])?\s*\x60?([^\s\x60:][^\x60]*?):(\d+)(?:\s*[-–]\s*(\d+))?\x60?(?:\s*\((?:before the change|old)\))?\s*[—–:-]+\s*(done|skipped|fixed|addressed|changed|declined)\b[\s:.,—–-]*(?:\(([^)]*)\))?\s*(.*)$/i;
/** Read the agent's answer: one `path:line — done|skipped (why)` per line. Anything else in the reply is ignored. */
export function parseResolutions(reply) {
    const out = [];
    for (const raw of reply.split(/\r?\n/)) {
        const line = raw.replace(/\*\*|__/g, "");
        const m = RESOLUTION.exec(line);
        if (!m)
            continue;
        const word = m[4].toLowerCase();
        const outcome = word === "skipped" || word === "declined" ? "skipped" : "done";
        const why = (m[5] ?? m[6] ?? "").trim();
        out.push({ path: m[1].trim(), line: Number(m[2]), ...(m[3] ? { endLine: Number(m[3]) } : {}), outcome, ...(why ? { why } : {}) });
    }
    return out;
}
/** Apply an answer to the notes that were waiting on it. A note the answer does not mention stays open. */
export function applyResolutions(notes, parsed, waitingOn) {
    return notes.map((n) => {
        if (n.status !== "sent" || (waitingOn && !waitingOn.has(n.id)))
            return n;
        const hit = parsed.find((r) => r.path === n.path && (r.line === n.line || (r.line >= n.line && r.line <= (n.endLine ?? n.line))));
        if (!hit)
            return n;
        return { ...n, status: hit.outcome, ...(hit.why ? { reply: hit.why } : {}) };
    });
}
/**
 * Where the notes go and what the button says. A running agent takes them as one message; with none running they wait
 * and ride on the next prompt the person sends ("3 notes attached"); on a pull request they are posted to it.
 */
export function planSend(target, notes) {
    const count = drafts(notes).length;
    if (!target || count === 0)
        return { mode: "none", count: 0, label: "Send notes" };
    const n = plural(count, "note");
    if (target.kind === "pull-request")
        return { mode: "pull-request", count, label: `Post ${n} to ${target.label}` };
    if (target.sessionId && target.running)
        return { mode: "send", count, agent: target.agent ?? target.label, label: `Send ${n} to ${target.agent ?? target.label}` };
    return { mode: "attach", count, label: `Attach ${n} to your next prompt` };
}
/** "3 notes attached", for the chip in the composer. */
export const attachedChip = (count) => `${plural(count, "note")} attached`;
/** The Timeline's line when a batch goes out. */
export const timelineLine = (count, agent) => `${plural(count, "review note")} sent to ${agent}`;
/** A branch or key as a file name: no separators, nothing a file system dislikes. */
export function reviewNotesFileName(key) {
    const safe = key.replace(/[^A-Za-z0-9._-]/g, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).replace(/^\.+/, "_");
    return `${safe.slice(0, 180) || "_"}.json`;
}
/** A stored document, checked: anything that does not fit comes back empty, never throws. */
export function readNotesDoc(raw, key) {
    const empty = { version: 1, key, notes: [] };
    if (!raw || typeof raw !== "object")
        return empty;
    const list = raw.notes;
    if (!Array.isArray(list))
        return empty;
    const notes = [];
    for (const r of list.slice(0, MAX_NOTES)) {
        if (!r || typeof r !== "object")
            continue;
        const n = r;
        if (typeof n.id !== "string" || typeof n.path !== "string" || !n.path || typeof n.text !== "string" || !n.text)
            continue;
        if (n.side !== "old" && n.side !== "new")
            continue;
        if (!Number.isInteger(n.line) || n.line < 1)
            continue;
        const status = n.status === "sent" || n.status === "done" || n.status === "skipped" ? n.status : "draft";
        notes.push({
            id: n.id.slice(0, 80),
            path: n.path.slice(0, 1000),
            side: n.side,
            line: n.line,
            ...(Number.isInteger(n.endLine) && n.endLine > n.line ? { endLine: n.endLine } : {}),
            text: n.text.slice(0, MAX_NOTE_CHARS),
            status,
            createdAt: typeof n.createdAt === "number" ? n.createdAt : 0,
            ...(typeof n.sentAt === "number" ? { sentAt: n.sentAt } : {}),
            ...(typeof n.sentTo === "string" ? { sentTo: n.sentTo.slice(0, 200) } : {}),
            ...(typeof n.reply === "string" ? { reply: n.reply.slice(0, 500) } : {}),
        });
    }
    return { version: 1, key, notes };
}
export function numberUnifiedDiff(diff) {
    const rows = [];
    let path;
    let oldNo = 0;
    let newNo = 0;
    let inHunk = false;
    for (const raw of diff.replace(/\r/g, "").replace(/\n$/, "").split("\n")) {
        if (raw.startsWith("diff --git ")) {
            const m = /^diff --git a\/(.*) b\/(.*)$/.exec(raw);
            path = m ? m[2] : undefined;
            inHunk = false;
            rows.push({ kind: "meta", text: raw });
            continue;
        }
        const h = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(raw);
        if (h) {
            oldNo = Number(h[1]);
            newNo = Number(h[2]);
            inHunk = true;
            rows.push({ kind: "hunk", text: raw, ...(path ? { path } : {}) });
            continue;
        }
        if (!inHunk || raw.startsWith("\\")) {
            if (!inHunk && raw.startsWith("+++ b/"))
                path = raw.slice(6);
            rows.push({ kind: "meta", text: raw });
            continue;
        }
        if (raw.startsWith("+"))
            rows.push({ kind: "add", text: raw.slice(1), ...(path ? { path } : {}), newNo: newNo++ });
        else if (raw.startsWith("-"))
            rows.push({ kind: "del", text: raw.slice(1), ...(path ? { path } : {}), oldNo: oldNo++ });
        else if (raw.startsWith(" "))
            rows.push({ kind: "context", text: raw.slice(1), ...(path ? { path } : {}), oldNo: oldNo++, newNo: newNo++ });
        else
            rows.push({ kind: "meta", text: raw });
    }
    return rows;
}
//# sourceMappingURL=reviewNotes.js.map